// components
import { DateNavigation } from 'components/shared';
import TableCRUDLayout from '../CRUD/TableCRUDLayout';

// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

// styles
import { DateNavigationContainer } from './AbsenceNotices.styles';

// utils
import dayjs from 'dayjs';
import { flattenToDate, getMonthInterval } from 'utils/date';
import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

const AbsenceNotices = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { startDate } = getMonthInterval(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(startDate);
  const monthInterval = getMonthInterval(selectedDate);
  const { data: absenceNotices, isLoading } = queries.useGetAbsenceNotices(
    dayjs(monthInterval.startDate).format('YYYY-MM-DD'),
    dayjs(monthInterval.endDate).format('YYYY-MM-DD'),
    undefined
  );
  const { mutate: processAbsenceNotice } = mutations.useProcessAbsenceNotice();

  const onClickProcess = (id: string) => {
    processAbsenceNotice({
      absenceNoticeId: id
    });
    queryClient.invalidateQueries(queryKeys.getAbsenceNotices.name);
  };

  const columns = [
    { field: 'employeeNumber', headerName: t('employeeNr'), minWidth: 40 },
    { field: 'employee', headerName: t('employee'), minWidth: 250 },
    { field: 'start', headerName: t('startTime'), minWidth: 120 },
    { field: 'end', headerName: t('endTime'), minWidth: 120 },
    { field: 'status', headerName: t('status'), minWidth: 40 },
    { field: 'createdOn', headerName: t('createdOn'), minWidth: 120 },
    { field: 'reason', headerName: t('reason'), flex: 1 }
  ];

  const rows =
    absenceNotices
      ?.filter((d) => !dayjs(d.start).isBefore(dayjs(monthInterval.startDate)))
      ?.sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      )
      .map((d) => ({
        id: d.entityId,
        employeeNumber: d.employee?.number,
        employee: d.employee?.nameInformal,
        status: t(`absenceNoticeStatus.${d.status}`),
        start: dayjs(d.start).format('DD-MM-YYYY HH:mm'),
        end: dayjs(d.end).format('DD-MM-YYYY HH:mm'),
        createdOn: dayjs(d.createdOn).format('DD-MM-YYYY HH:mm'),
        reason: d.reason
      })) ?? [];
  return (
    <>
      <DateNavigationContainer>
        <DateNavigation
          defaultPeriod="month"
          defaultDate={selectedDate}
          onDateChange={(date) => setSelectedDate(flattenToDate(date))}
        />
      </DateNavigationContainer>

      <TableCRUDLayout
        columns={columns}
        rows={rows}
        editable={false}
        loading={isLoading}
        customActions={(id, row) => [
          ...(row?.status && row.status == t('absenceNoticeStatus.RECEIVED')
            ? [
                {
                  tooltipTitle: t('process'),
                  icon: 'check',
                  onClick: () => onClickProcess(id as string)
                }
              ]
            : [])
        ]}
      />
    </>
  );
};

export default AbsenceNotices;
